import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState({ next: "", confirm: "" });
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.next.length < 8) return setError("Password must be at least 8 characters.");
    if (pw.next !== pw.confirm) return setError("Passwords don't match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw.next });
      setDone(true);
      setTimeout(() => navigate("/login"), 1500);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8] flex items-center justify-center p-8">
      <div className="w-full max-w-sm space-y-6">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase text-[#5C5C5C]">Choose a new password</div>
          <h1 className="font-display text-3xl mt-2">Reset password</h1>
        </div>

        {!token && (
          <div data-testid="reset-no-token" className="text-sm text-[#E63946]">
            No reset token in the URL. Please use the link from your email.
          </div>
        )}

        {done ? (
          <div data-testid="reset-done" className="card-flat p-6 text-sm">
            Password updated. Redirecting to sign in…
          </div>
        ) : (
          <form data-testid="reset-form" onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="np">New password</Label>
              <Input
                id="np"
                data-testid="reset-new"
                type="password" required minLength={8}
                value={pw.next}
                onChange={(e) => setPw({ ...pw, next: e.target.value })}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cf">Confirm new password</Label>
              <Input
                id="cf"
                data-testid="reset-confirm"
                type="password" required minLength={8}
                value={pw.confirm}
                onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                className="h-11"
              />
            </div>
            {error && <div data-testid="reset-error" className="text-sm text-[#E63946]">{error}</div>}
            <Button
              data-testid="reset-submit"
              type="submit"
              disabled={loading || !token}
              className="w-full h-11 rounded-full bg-[#2F4F4F] hover:bg-[#213838] text-[#FDFCF8]"
            >
              {loading ? "Updating..." : "Set new password"}
            </Button>
            <div className="text-sm text-center text-[#5C5C5C]">
              <Link to="/login" className="text-[#2F4F4F] underline underline-offset-4">
                Back to sign in
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
